/**
 * Damit die Tastatur nicht die Eingabezeile verdeckt.
 *
 * Aus dem Betrieb, auf dem iPhone: Chat öffnen, ins Feld tippen — und das Feld
 * liegt unter der Tastatur. Auf dem iPad passt es.
 *
 * Der Grund ist ein alter Unterschied in iOS-Safari: die Tastatur verkleinert
 * dort **nicht das Fenster**, sondern legt sich darüber. `height: 100%` misst
 * weiter die volle Höhe, die Anwendung reicht also hinter die Tastatur, und
 * was unten sitzt — hier die Eingabezeile — bleibt darunter liegen.
 *
 * Gemessen wird deshalb nicht die Höhe, sondern die **Überdeckung**: was das
 * Fenster hat und die sichtbare Fläche nicht. Genau dieser Betrag steht als
 * `--tastatur-hoehe` bereit, und die Anwendung lässt ihn unten frei.
 *
 * Der erste Anlauf hat stattdessen die Höhe von `html`, `body` und `#root` auf
 * die sichtbare Fläche gesetzt — und damit einen schlimmeren Fehler gebaut:
 * eine Anwendung, die kleiner ist als das Fenster, liegt irgendwo darin, und
 * wo, entscheidet iOS. Schiebt Safari die Fläche, um das Eingabefeld
 * freizustellen, rutscht sie aus dem Blick. Auf dem Gerät war dann gar nichts
 * mehr zu sehen, nur noch die Tastatur. Ein Abstand kann das nicht: die
 * Anwendung bleibt so groß wie das Fenster und an ihrem Platz. Stimmt der
 * Betrag einmal nicht, steht man da, wo man vorher stand — mehr nicht.
 *
 * Nicht genommen wurden zwei naheliegende Wege: `100dvh` kennt die Tastatur
 * nicht, nur die Browserleisten, und `interactive-widget=resizes-content` im
 * Viewport-Meta wäre ein zweiter Mechanismus neben diesem, dessen Rückhalt in
 * Safari offen ist — zwei, die sich in die Quere kommen können, sind schlechter
 * als einer.
 */

/**
 * Darunter ist es eine Browserleiste und keine Tastatur.
 *
 * **Die Schwelle allein reicht nicht**, und das kam aus dem Betrieb: unter der
 * Eingabezeile blieb ein Streifen leer. Eine überlagernde Browserleiste macht
 * dieselbe Differenz wie eine kleine Tastatur; die Zahl trennt die Fälle
 * nicht. Sie bleibt trotzdem stehen, gegen Ausreißer in der Messung.
 */
const SCHWELLE = 80

/**
 * Steht der Cursor wirklich in einem Schreibfeld?
 *
 * **Ohne Fokus keine Tastatur.** Das ist kein Schwellenwert, sondern die
 * Sache selbst -- und der einzige Weg, eine Tastatur von einer Browserleiste
 * zu unterscheiden, die sich über die Seite legt.
 */
function schreibtJemand(): boolean {
  const el = document.activeElement
  if (!(el instanceof HTMLElement)) return false
  return (
    el instanceof HTMLInputElement
    || el instanceof HTMLTextAreaElement
    || el.isContentEditable
  )
}

export function tastaturBeobachten(): void {
  const sicht = window.visualViewport
  if (!sicht) {
    // Ohne die Schnittstelle bleibt alles, wie es war: kein Abstand.
    return
  }

  const anpassen = () => {
    // `offsetTop` bleibt hier bewusst außen vor: verschiebt iOS die sichtbare
    // Fläche, ändert das ihre Höhe nicht -- die Differenz ist weiterhin genau
    // die Tastatur.
    const roh = window.innerHeight - sicht.height

    // Nach oben begrenzt, damit ein Ausreißer in der Messung die Anwendung
    // nicht zusammenfalten kann. Der Abstand ist eine Hilfe, keine Gewalt.
    const deckel = window.innerHeight / 2
    const abstand = roh < SCHWELLE || !schreibtJemand()
      ? 0
      : Math.min(Math.round(roh), Math.round(deckel))

    document.documentElement.style.setProperty('--tastatur-hoehe', `${abstand}px`)
  }

  anpassen()
  sicht.addEventListener('resize', anpassen)
  sicht.addEventListener('scroll', anpassen)
  // **Beim Verlassen sofort messen.** Die sichtbare Fläche wächst danach zwar
  // ohnehin und löst ein `resize` aus -- aber erst, wenn die Tastatur
  // heruntergefahren ist. Bis dahin stünde der Abstand noch da, obwohl längst
  // niemand mehr schreibt.
  //
  // `focusin` ebenso: wer aus einem Feld in ein anderes wechselt, hat
  // durchgehend eine Tastatur, und ohne dieses Ereignis fiele der Abstand
  // dazwischen kurz weg.
  window.addEventListener('focusin', anpassen)
  window.addEventListener('focusout', anpassen)
}
